import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useOutletContext } from 'react-router-dom';
import { Typography, Box } from '@mui/material';
import { acme, acmePageSectionCardSx, acmeBillingSectionContentSx } from '../../theme/acmeTheme';
import { Card, CardContent, Button } from '../ui';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import CampaignListDashboard from '../common/CampaignListDashboard';
import EmailAnalyticsDialog from '../EmailAnalyticsDialog';
import { useCampaignListDashboard } from '../../hooks/useCampaignListDashboard';
import { isAdminRole } from '../../utils/userRoles';

const CampaignDashboard = () => {
  const { user } = useOutletContext();
  const snackbar = usePageSnackbar();
  const list = useCampaignListDashboard('email', snackbar);

  const [queueStatus, setQueueStatus] = useState({
    waiting: 0,
    active: 0,
    completed: 0,
    failed: 0,
    totalJobs: 0,
  });
  const [clearingQueue, setClearingQueue] = useState(false);

  const fetchQueueStatus = async () => {
    try {
      const response = await axios.get('/api/send-email/queue-status');
      setQueueStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    }
  };

  const clearQueue = async (type = 'failed') => {
    setClearingQueue(true);
    try {
      const response = await axios.post('/api/send-email/clear-queue', { type });
      snackbar.setSuccessMessage(
        `Queue cleared: ${JSON.stringify(response.data.result)}`,
      );
      fetchQueueStatus();
    } catch (error) {
      console.error('Failed to clear queue:', error);
      snackbar.setErrorMessage('Failed to clear queue');
    } finally {
      setClearingQueue(false);
    }
  };

  useEffect(() => {
    fetchQueueStatus();
    const queueInterval = setInterval(fetchQueueStatus, 5000);
    return () => clearInterval(queueInterval);
  }, []);

  const queuePanel =
    isAdminRole(user?.role) ? (
      <Card sx={{ ...acmePageSectionCardSx, mb: 2 }}>
        <CardContent sx={acmeBillingSectionContentSx}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Email Queue Status
        </Typography>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
          <Box display="flex" gap={3} flexWrap="wrap">
            <Box textAlign="center">
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                Waiting
              </Typography>
              <Typography variant="h6" sx={{ color: '#ed6c02' }}>
                {queueStatus.waiting}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                Active
              </Typography>
              <Typography variant="h6" sx={{ color: acme.colors.brand }}>
                {queueStatus.active}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                Completed
              </Typography>
              <Typography variant="h6" sx={{ color: acme.colors.brand }}>
                {queueStatus.completed}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                Failed
              </Typography>
              <Typography variant="h6" sx={{ color: acme.colors.destructive }}>
                {queueStatus.failed}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="body2" sx={{ color: acme.colors.mutedForegroundSolid }}>
                Total Jobs
              </Typography>
              <Typography variant="h6">{queueStatus.totalJobs}</Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Button
              variant="outline"
              size="small"
              disabled={clearingQueue || queueStatus.failed === 0}
              onClick={() => clearQueue('failed')}
            >
              Clear Failed
            </Button>
            <Button
              variant="outline"
              size="small"
              disabled={clearingQueue || queueStatus.completed === 0}
              onClick={() => clearQueue('completed')}
            >
              Clear Completed
            </Button>
            <Button
              variant="outline"
              size="small"
              disabled={clearingQueue || queueStatus.totalJobs === 0}
              onClick={() => clearQueue('all')}
              sx={{ color: acme.colors.destructive, borderColor: acme.colors.destructive }}
            >
              Clear All
            </Button>
          </Box>
        </Box>
        </CardContent>
      </Card>
    ) : null;

  return (
    <CampaignListDashboard
      list={list}
      headerExtra={queuePanel}
      AnalyticsDialog={EmailAnalyticsDialog}
      PageSnackbars={PageSnackbars}
      snackbarProps={snackbar.snackbarProps}
    />
  );
};

export default CampaignDashboard;
